import React, { useState } from "react";
import { convert } from "./converter.js";
import { encodingLabel } from "./encoding-choices.js";
import { useApp } from "./common.jsx";
import { useTranslate } from "./common.jsx";

const rows = [
  [["`", "~"], ["1", "!"], ["2", "@"], ["3", "#"], ["4", "$"], ["5", "%"], ["6", "^"], ["7", "&"], ["8", "*"], ["9", "("], ["0", ")"], ["-", "_"], ["=", "+"], ["\\", "|"]],
  [["q", "Q"], ["w", "W"], ["e", "E"], ["r", "R"], ["t", "T"], ["y", "Y"], ["u", "U"], ["i", "I"], ["o", "O"], ["p", "P"], ["[", "{"], ["]", "}"]],
  [["a", "A"], ["s", "S"], ["d", "D"], ["f", "F"], ["g", "G"], ["h", "H"], ["j", "J"], ["k", "K"], ["l", "L"], [";", ":"], ["'", '"']],
  [["z", "Z"], ["x", "X"], ["c", "C"], ["v", "V"], ["b", "B"], ["n", "N"], ["m", "M"], [",", "<"], [".", ">"], ["/", "?"]],
];
const offsets = [0, 14, 26, 37];

function shownValue(code) {
  if (typeof code !== "number") return "·";
  const ch = String.fromCodePoint(code);
  return /[\p{C}\p{Zl}\p{Zp}]/u.test(ch) ? "·" : ch;
}

export function EncodingKeyboard({ from, to, input, onInput }) {
  const t = useTranslate(), {lang}=useApp();
  const [side, setSide] = useState("from");
  const [shift, setShift] = useState(false);
  const encoding = side === "from" ? from : to;
  const insert = (code) => {
    if (side !== "from" || typeof code !== "number") return;
    const ch = String.fromCodePoint(code);
    const el = document.getElementById("convertinput");
    const start = el?.selectionStart ?? input.length;
    const end = el?.selectionEnd ?? start;
    onInput(input.slice(0, start) + ch + input.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + ch.length, start + ch.length);
    });
  };
  return (
    <section className="encoding-keyboard" aria-labelledby="encoding-keyboard-title">
      <div className="encoding-keyboard-head">
        <div>
          <p className="encoding-kicker">{t("TYPE BY KEY POSITION", "კლავიატურის განლაგება")}</p>
          <h2 id="encoding-keyboard-title">{t("Keyboard map", "კლავიატურის რუკა")}</h2>
          <p className="encoding-intro">{t("Compare a reference keyboard layout with the characters stored by this encoding.", "შეადარეთ კლავიატურის განლაგება და ამ კოდირებით შენახული სიმბოლოები.")}</p>
        </div>
        <div className="encoding-controls">
          <label>
            <span>{t("Show mapping for", "კოდირება")}</span>
            <select value={side} onChange={(e) => setSide(e.target.value)} aria-label={t("Show source or target encoding", "აირჩიეთ საწყისი ან სამიზნე კოდირება")}>
              <option value="from">{t("Source", "წყარო")} · {encodingLabel(from,lang)}</option>
              <option value="to">{t("Target", "შედეგი")} · {encodingLabel(to,lang)}</option>
            </select>
          </label>
          <button className={`encoding-shift${shift ? " active" : ""}`} type="button" aria-pressed={shift} onClick={() => setShift(!shift)}>
            ⇧ <span>Shift</span>
          </button>
        </div>
      </div>
      <div className="keyboard-frame" data-shift={shift ? "on" : "off"}>
        {rows.map((row, ri) => (
          <div className={`keyboard-row row-${ri + 1}`} key={ri}>
            {row.map(([base, upper], ci) => {
              const physical=shift?upper:base;
              const isLegacyLayout=encoding==="latin-russian"||encoding==="latin-typewriter";
              const stored=isLegacyLayout?physical:convert(physical,"_eng_",encoding);
              const code=stored.codePointAt(0);
              const value = shownValue(code);
              const semantic = isLegacyLayout?convert(physical,encoding,"_utf8_"):convert(physical,"_eng_","_utf8_");
              const codeLabel = typeof code === "number" ? `U+${code.toString(16).toUpperCase().padStart(4, "0")}` : t("unmapped", "არ არის");
              const enabled = side === "from" && typeof code === "number";
              const accessible = `${base}${upper === base ? "" : ` / ${upper}`}: Georgian ${semantic}; ${value === "·" ? t("no character", "სიმბოლო არ არის") : value}, ${codeLabel}`;
              return <button type="button" className={`map-key${enabled ? " insertable" : ""}${typeof code !== "number" ? " unmapped" : ""}`} key={base} disabled={!enabled} aria-label={accessible} title={accessible} onClick={() => insert(code)}>
                <span className="key-top"><span className="physical-key">{base}<small>{upper}</small></span><span className="semantic-key">{value}</span></span>
                <span className="mapped-key">{semantic}</span>
                <span className="codepoint-key">{codeLabel}</span>
              </button>;
            })}
          </div>
        ))}
        <div className="keyboard-row keyboard-space"><button type="button" className="map-key space-key" disabled={side!=="from"} onClick={()=>insert(32)}><span className="physical-key">Space</span><span className="mapped-key">{t("Space","გამოტოვება")}</span></button></div>
      </div>
      <div className="keyboard-legend">
        <span><i className="legend-dot mapped-dot" />{t("Top left: physical key · center: Georgian character · top right: stored character · bottom: code point", "ზედა მარცხენა: კლავიში · ცენტრი: ქართული სიმბოლო · ზედა მარჯვენა: შენახული სიმბოლო · ქვედა: კოდი")}</span>
        {side === "from" ? <span>{t("Click a mapped key to insert it into the source text.", "კლავიშზე დაჭერით სიმბოლო დაემატება საწყის ტექსტს.")}</span> : <span>{t("Target view is for reference; click Source to insert characters.", "შედეგის რუკა საცნობაროა; ჩასასმელად აირჩიეთ წყარო.")}</span>}
        <span className="legacy-note">{t("Reference layout, not an operating-system keyboard setting. Legacy fonts may use different character positions.", "ეს საცნობარო განლაგებაა და არა სისტემის კლავიატურის პარამეტრი. ძველ შრიფტებს შესაძლოა განსხვავებული პოზიციები ჰქონდეს.")}</span>
      </div>
    </section>
  );
}
